/**
 * Per-user route access. ENFORCED (in the interface).
 *
 * Access is granted per user, not per role: the session carries
 * `user.access_control`, a flat array of the route paths that person may open,
 * written by an administrator on the user form:
 *
 *   access_control: ['/dashboard', '/hiring', '/hiring/import']
 *
 * Two people with the same role can therefore reach different pages, which is the
 * whole point — the role says what kind of account it is, and this array says
 * where it may go.
 *
 * Two things enforce it, and both are needed:
 *  - `getNavRoutes` hides what a user cannot reach, so the sidebar never offers a
 *    dead end;
 *  - the branch guard in `ProtectedRoute` checks the matched route, so typing the
 *    URL is refused too. Hiding a nav entry is presentation, not access control.
 *
 * None of this is a security boundary: the API enforces its own rules by role and
 * by pipeline config, and this decides what is worth showing.
 */

/** The backend's fixed role list (`constants/roles.js` on the server). */
export const ROLES = {
  admin: 'admin',
  manager: 'manager',
  recruiter: 'recruiter',
  contentWriter: 'content-writer',
  marketer: 'marketer',
}

/**
 * The grantable routes (`constants/permissions.js` on the server). Some are not
 * pages: they gate controls inside a page — the CSV import dialog, the assignment
 * actions, and the control that turns staged Meta leads into applicants.
 *
 * A page gated by ROLE rather than by a grant is deliberately absent — see
 * `hasRouteAccess`. Listing one here would offer an administrator a checkbox
 * that decides nothing.
 */
export const ROUTE_PATHS = {
  dashboard: '/dashboard',
  jobListings: '/job-listings',
  hiring: '/hiring',
  hiringImport: '/hiring/import',
  hiringAssign: '/hiring/assign',
  interviews: '/interviews',
  hired: '/hired',
  inbox: '/inbox',
  meta: '/meta',
  metaImport: '/meta/import',
  screening: '/screening',
  bot: '/bot',
  users: '/users',
}

/** Administrators bypass every check. */
const isAdmin = (user) => user?.role === ROLES.admin

/**
 * The routes this user may open. Both spellings are read because the portal API
 * spells it `acess_control`; a list of objects from an older session is tolerated
 * by reading `.path` off each entry, so a stale token does not lock someone out
 * until they sign in again.
 */
const routesOf = (user) => {
  const routes = user?.access_control ?? user?.acess_control
  if (!Array.isArray(routes)) return []
  return routes.map((entry) => (typeof entry === 'string' ? entry : entry?.path)).filter(Boolean)
}

/** May this user open `path`? */
export const canAccessRoute = (user, path) => {
  if (!user) return false
  if (isAdmin(user)) return true
  return routesOf(user).includes(path)
}

/**
 * A route is allowed when it declares no `access` path, or when the user holds
 * the one it names — and, separately, when it declares no `roles` or the user
 * holds one of them.
 *
 * `roles` exists for a page that belongs to a job rather than to a permission:
 * a recruiter's own figures are theirs by virtue of being a recruiter, and
 * putting that behind `access_control` would mean handing the grant out by hand
 * to every new hire and quietly having some of them without it.
 *
 * Deliberately NOT subject to the administrator bypass, unlike `access`. A
 * roles-gated page is one that only makes sense for that role — the self
 * dashboard has no answer for somebody who owns no leads — so showing it to an
 * administrator would put a page in the nav that cannot render.
 */
export const hasRouteAccess = (route, user) => {
  if (!user) return false
  if (route?.roles && !route.roles.includes(user.role)) return false
  if (!route?.access) return true
  return canAccessRoute(user, route.access)
}

/**
 * The deepest matched route that declares an `access` path or a `roles` list
 * decides — a child may narrow its parent, and inheriting the parent's would let
 * it widen again. Returns `null` when nothing on the matched path is restricted.
 */
export const findRestrictedMatch = (matches = []) =>
  [...matches].reverse().find((match) => match.handle?.access || match.handle?.roles) ?? null

/** Navigation entries the user may see, flattened out of the route tree. */
export const getNavRoutes = (routes, user, section = 'main') => {
  const items = []

  const walk = (nodes) => {
    for (const route of nodes) {
      if (!hasRouteAccess(route, user)) continue

      if (route.showInNav && route.path) {
        items.push({
          to: route.path,
          label: route.name,
          icon: route.icon,
          section: route.section ?? 'main',
        })
      } else if (route.children) {
        walk(route.children)
      }
    }
  }

  walk(routes)
  return items.filter((item) => item.section === section)
}

/* --------------------------------------------------------- the catalogue --- */

/**
 * What a grant IS, which is the difference between "may open" and "may do".
 *
 * A `page` is somewhere to go: withholding it hides a nav entry and refuses the
 * URL. An `action` is a control inside a page somebody can already open — the CSV
 * import dialog, the run-assignment button, the Meta promote control. Granting the
 * page without the action is the normal case, and the reason they are separate.
 */
export const ROUTE_KINDS = { page: 'page', action: 'action' }

/**
 * Every grantable route, and what each one is.
 *
 * Lives HERE, on the client, because this is where the routes exist. The server
 * used to keep a copy to validate against and the two drifted — the WhatsApp inbox
 * was grantable server-side and absent from the form, so nobody could be given it.
 * The server now stores whatever array this side sends, which means adding a page
 * is one edit in one file.
 *
 * `parent` names the page an action belongs to, so the form can group it there
 * rather than list it as a page of its own.
 */
export const ROUTE_CATALOG = [
  {
    path: ROUTE_PATHS.dashboard,
    kind: ROUTE_KINDS.page,
    label: 'Overview',
    description: 'Workspace analytics and dashboard reports',
  },
  {
    path: ROUTE_PATHS.jobListings,
    kind: ROUTE_KINDS.page,
    label: 'Job listings',
    description: 'Browse, create and edit job listings',
  },
  {
    path: ROUTE_PATHS.hiring,
    kind: ROUTE_KINDS.page,
    label: 'Hiring pipeline',
    description:
      'The lead queue. Which leads appear is decided by the pipeline config, not by this grant.',
  },
  {
    path: ROUTE_PATHS.hiringImport,
    kind: ROUTE_KINDS.action,
    parent: ROUTE_PATHS.hiring,
    label: 'Import leads from CSV',
    description: 'Upload a file and map its columns onto leads',
  },
  {
    path: ROUTE_PATHS.hiringAssign,
    kind: ROUTE_KINDS.action,
    parent: ROUTE_PATHS.hiring,
    label: 'Assign and reassign leads',
    description: 'Run the assignment sweep, and move a lead to a different recruiter',
  },
  {
    path: ROUTE_PATHS.interviews,
    kind: ROUTE_KINDS.page,
    label: 'Interview desk',
    description:
      'The interview calendar, by week and department. Whose calendar appears is decided by role — a recruiter sees their own — not by this grant.',
  },
  {
    path: ROUTE_PATHS.hired,
    kind: ROUTE_KINDS.page,
    label: 'Hired',
    description:
      'The roster of hires, by department. Whose hires appear is decided by role — a recruiter sees their own — not by this grant.',
  },
  {
    path: ROUTE_PATHS.inbox,
    kind: ROUTE_KINDS.page,
    label: 'WhatsApp inbox',
    description:
      'The conversations for the leads this person owns. Which threads appear is decided by lead ownership, not by this grant.',
  },
  {
    path: ROUTE_PATHS.meta,
    kind: ROUTE_KINDS.page,
    label: 'Meta ads',
    description: 'Campaign performance, and the leads waiting to be brought into the pipeline',
  },
  {
    path: ROUTE_PATHS.metaImport,
    kind: ROUTE_KINDS.action,
    parent: ROUTE_PATHS.meta,
    label: 'Sync and import Meta leads',
    description: 'Pull leads from Meta, and promote staged ones into the pipeline',
  },
  {
    path: ROUTE_PATHS.screening,
    kind: ROUTE_KINDS.page,
    label: 'Screening scripts',
    description:
      "The bot's screening scripts and message templates. Editing them stays administrator-only whoever holds this grant.",
  },
  {
    path: ROUTE_PATHS.bot,
    kind: ROUTE_KINDS.page,
    label: 'Scheduling bot',
    description: 'What the WhatsApp bot is doing now and what its screening has produced',
  },
  {
    path: ROUTE_PATHS.users,
    kind: ROUTE_KINDS.page,
    label: 'Users',
    description: 'Accounts, roles, pipeline config and route access',
  },
]

export const ROUTE_PAGES = ROUTE_CATALOG.filter((entry) => entry.kind === ROUTE_KINDS.page)
export const ROUTE_ACTIONS = ROUTE_CATALOG.filter((entry) => entry.kind === ROUTE_KINDS.action)
